import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { inventarioService, Existencia, AjusteInventario } from '../../services/inventarioService';
import { productoService } from '../../services/productoService';
import { sucursalService } from '../../services/sucursalService';
import { useAuthStore } from '../../store/authStore';

interface AjusteInventarioFormProps {
  existencia?: Existencia | null;
  onClose: () => void;
}

const AjusteInventarioForm = ({ existencia, onClose }: AjusteInventarioFormProps) => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const { data: productosData } = useQuery(
    'productos',
    () => productoService.getAll(1, 100)
  );

  const { data: sucursalesData } = useQuery(
    'sucursales',
    () => sucursalService.getAll(1, 100)
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm<AjusteInventario>({
    defaultValues: existencia ? {
      productoId: existencia.productoId,
      sucursalId: existencia.sucursalId,
      cantidad: 0,
      tipo: 'ajuste'
    } : {
      productoId: 0,
      sucursalId: 0,
      cantidad: 0,
      tipo: 'ajuste'
    }
  });

  const tipo = watch('tipo');
  const cantidad = watch('cantidad');

  useEffect(() => {
    if (existencia) {
      reset({
        productoId: existencia.productoId,
        sucursalId: existencia.sucursalId,
        cantidad: 0,
        tipo: 'ajuste'
      });
    }
  }, [existencia, reset]);

  const ajustarMutation = useMutation(inventarioService.ajustarInventario, {
    onSuccess: () => {
      toast.success('Inventario ajustado correctamente');
      queryClient.invalidateQueries(['existencias']);
      queryClient.invalidateQueries(['movimientos']);
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Error al ajustar inventario');
    }
  });

  const onSubmit = (data: AjusteInventario) => {
    // Si el tipo es 'salida' y la cantidad es positiva, convertirla a negativa
    let cantidadFinal = data.cantidad;
    if ((tipo === 'salida' || tipo === 'merma') && cantidadFinal > 0) {
      cantidadFinal = -cantidadFinal;
    }

    ajustarMutation.mutate({
      ...data,
      cantidad: cantidadFinal
    });
  };

  const tipos = [
    { value: 'entrada', label: 'Entrada' },
    { value: 'salida', label: 'Salida' },
    { value: 'ajuste', label: 'Ajuste' },
    { value: 'merma', label: 'Merma' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">
          {existencia ? 'Ajustar Inventario' : 'Nuevo Ajuste de Inventario'}
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {!existencia && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Producto *
                </label>
                <select
                  {...register('productoId', { 
                    required: 'Producto es requerido',
                    valueAsNumber: true
                  })}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value={0}>Selecciona un producto</option>
                  {productosData?.data.productos
                    .filter(p => p.manejaInventario)
                    .map((producto) => (
                      <option key={producto.id} value={producto.id}>
                        {producto.name} {producto.sku ? `(${producto.sku})` : ''}
                      </option>
                    ))}
                </select>
                {errors.productoId && (
                  <p className="text-red-500 text-sm mt-1">{errors.productoId.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sucursal *
                </label>
                <select
                  {...register('sucursalId', { 
                    required: 'Sucursal es requerida',
                    valueAsNumber: true
                  })}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value={0}>Selecciona una sucursal</option>
                  {sucursalesData?.data.sucursales.map((sucursal) => (
                    <option key={sucursal.id} value={sucursal.id}>
                      {sucursal.name}
                    </option>
                  ))}
                </select>
                {errors.sucursalId && (
                  <p className="text-red-500 text-sm mt-1">{errors.sucursalId.message}</p>
                )}
              </div>
            </>
          )}

          {existencia && (
            <div className="bg-gray-50 p-4 rounded">
              <p className="text-sm text-gray-600">
                <strong>Producto:</strong> {existencia.producto?.name}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Sucursal:</strong> {existencia.sucursal?.name}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Stock Actual:</strong> {(Number(existencia.cantidad) || 0).toFixed(3)} {existencia.producto?.unidadMedida}
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Movimiento *
            </label>
            <select
              {...register('tipo', { required: 'Tipo es requerido' })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {tipos.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cantidad *
              {(tipo === 'salida' || tipo === 'merma') && (
                <span className="text-xs text-gray-500 ml-2">(Se aplicará como negativa)</span>
              )}
            </label>
            <input
              type="number"
              step="0.001"
              min="0"
              {...register('cantidad', { 
                required: 'Cantidad es requerida',
                valueAsNumber: true,
                min: { value: 0.001, message: 'Debe ser mayor a 0' }
              })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.cantidad && (
              <p className="text-red-500 text-sm mt-1">{errors.cantidad.message}</p>
            )}
            {existencia && tipo === 'salida' && cantidad > existencia.cantidad && (
              <p className="text-yellow-600 text-sm mt-1">
                ⚠️ La cantidad excede el stock disponible
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Costo Unitario (opcional)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              {...register('costoUnitario', { valueAsNumber: true })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder={existencia && existencia.costoPromedio ? `Actual: $${(Number(existencia.costoPromedio) || 0).toFixed(2)}` : ''}
            />
            <p className="text-xs text-gray-500 mt-1">
              Solo para movimientos de entrada. Si no se especifica, se usará el costo promedio actual.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Motivo
            </label>
            <textarea
              {...register('motivo')}
              rows={3}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Descripción del ajuste..."
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={ajustarMutation.isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {ajustarMutation.isLoading ? 'Procesando...' : 'Aplicar Ajuste'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AjusteInventarioForm;

